<script setup lang="ts">
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';
import TaskStepTitle from '@/components/TaskStepTitle/TaskStepTitle.vue';
import { FormMode } from '@/components/forms/types';
import { ParameterCombinationTableDto } from '@/api/generator';
import { mdiPlus } from '@mdi/js';
import Button from '@/components/buttons/Button.vue';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import { computed, ref, watch } from 'vue';
import { useField } from 'vee-validate';
import { useTaskStepTitle } from '@/components/TaskStepTitle/useTaskStepTitles';

import ConditionClause from './ConditionClause.vue';
import { Clause, operatorLabels, parseCondition, serializeCondition } from './condition';
import {
  cacheParamInfo,
  enumValueTitle,
  getCachedParamInfo,
  getParamInfos,
  getParamIds,
  clauseErrorFor,
  isClauseApplicable,
  matchesCondition,
} from './combinationMatch';

const props = defineProps<{
  name: string;
  combinationTable?: ParameterCombinationTableDto;
  editable: 'enabled' | 'disabled';
  mode: FormMode;
}>();

// Validated on mount so the form keeps the condition's errors (vee-validate drops them for unvalidated paths).
const { value: conditionString, handleChange } = useField<string>(() => props.name, undefined, {
  validateOnMount: true,
});

const isEditable = computed(() => props.editable === 'enabled' && props.mode !== 'details');

const clauses = ref<Clause[]>([]);

const unsupported = ref(false);

// Own writes are not re-parsed, so incomplete clauses survive the round trip through the field.
let lastWritten: string | undefined;

watch(
  conditionString,
  (value) => {
    if (value === lastWritten) return;

    const result = parseCondition(value);
    unsupported.value = result === 'unsupported';
    if (result !== 'unsupported') clauses.value = result;
  },
  { immediate: true },
);

function write() {
  const serialized = serializeCondition(clauses.value, (clause) => !stateFor(clause).error);
  lastWritten = serialized;
  unsupported.value = false;
  handleChange(serialized);
}

function updateClause(index: number, clause: Clause) {
  clauses.value[index] = clause;
  write();
}

function addClause() {
  clauses.value.push({ operator: '===' });
  write();
}

function removeClause(index: number) {
  clauses.value.splice(index, 1);
  write();
}

function replaceCondition() {
  clauses.value = [];
  write();
}

const combinations = computed(() => props.combinationTable?.combinations ?? []);

const liveParamInfos = computed(() => getParamInfos(combinations.value));

// Caches every param in the table, including ones no clause references yet.
watch(
  combinations,
  () => liveParamInfos.value.forEach((info, paramId) => cacheParamInfo(paramId, info)),
  { immediate: true },
);

const paramIds = computed(() => getParamIds(combinations.value));

function stateFor(clause: Clause) {
  return {
    // Cached info keeps the label of a param that was removed from the setup.
    paramInfo: clause.param
      ? (liveParamInfos.value.get(clause.param) ?? getCachedParamInfo(clause.param))
      : undefined,
    applicable: isClauseApplicable(clause, paramIds.value),
    error: clauseErrorFor(clause, liveParamInfos.value, paramIds.value),
  };
}

const clauseStates = computed(() => clauses.value.map(stateFor));

const paramItems = computed(() =>
  paramIds.value.map((paramId) => ({
    title: liveParamInfos.value.get(paramId)?.label ?? paramId,
    value: paramId,
  })),
);

const allValid = computed(() => clauseStates.value.every((state) => !state.error));

const matchedCombinations = computed(() => {
  if (unsupported.value || !allValid.value) return [];

  return combinations.value.filter(
    (combination) => combination.isEnabled && matchesCondition(conditionString.value, combination),
  );
});

const { getTitle } = useTaskStepTitle(computed(() => props.combinationTable?.combinations || []));

function clauseText(clause: Clause): string {
  const info = clause.param ? liveParamInfos.value.get(clause.param) : undefined;
  const enumTitle = clause.param ? enumValueTitle(clause.param, clause.value) : undefined;

  return `${info?.label ?? clause.param} ${operatorLabels[clause.operator]} ${enumTitle ?? clause.value}`;
}
</script>

<template>
  <div class="bg-white rounded elevation-2 pa-5 mb-4">
    <v-row>
      <v-col cols="12" md="8">
        <h6 class="mb-2">{{ $t('Condition_other') }}</h6>

        <template v-if="unsupported">
          <InlineNotification
            :value="{ type: 'warning', message: $t('ConditionCannotBeEditedMsg') }"
          >
            <template v-if="isEditable" #append>
              <Button
                variant="text"
                class="mt-2"
                :label="$t('ReplaceCondition')"
                @click="replaceCondition"
              />
            </template>
          </InlineNotification>
        </template>

        <template v-else-if="isEditable">
          <ConditionClause
            v-for="(clause, index) in clauses"
            :key="index"
            :clause="clause"
            :paramItems="paramItems"
            :paramInfo="clauseStates[index].paramInfo"
            :valueTitle="clause.param ? enumValueTitle(clause.param, clause.value) : undefined"
            :applicable="clauseStates[index].applicable"
            :error="clauseStates[index].error"
            class="mb-2"
            @update:clause="updateClause(index, $event)"
            @remove="removeClause(index)"
          />

          <InlineValidationError
            :name="name"
            ignoreTouched
            validateOnMount
            class="inline-error dense mb-2"
          />

          <Button
            variant="secondary"
            size="small"
            :icon="mdiPlus"
            iconPosition="start"
            :label="$t('AddCondition')"
            class="mb-2"
            @click="addClause"
          />
        </template>

        <template v-else>
          <p v-for="(clause, index) in clauses" :key="index">{{ clauseText(clause) }}</p>
        </template>
      </v-col>

      <v-col cols="12" md="4">
        <h6 class="mb-2">{{ $t('AppliedForCombinations') }}</h6>

        <TaskStepTitle
          v-for="combination in matchedCombinations"
          :key="combination.hash ?? 'noHash'"
          :combination="getTitle(combination)"
          class="mb-2"
        />

        <p
          v-if="!matchedCombinations.length"
          class="text-body-1 text-primary-lighten-2 font-weight-semibold"
        >
          {{ $t('NoData') }}
        </p>
      </v-col>
    </v-row>
  </div>
</template>
